import { GridActionsCellItem, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { compareDesc, format, parseISO } from "date-fns";
import { SyntheticEvent } from "react";
import { EmployeeTrainingStatus } from "../../constants";
import AttachmentIcon from "../../resources/images/icons-attachment.svg";
import MoreVertIcon from "../../resources/images/icons-more-vertical.svg";
import type { BaseTableRow, TableColumnDef, TableProps } from "../../types/components/table.types";
import { getHeaderName } from "../../types/components/table.types";
import { highlightKeyword } from "../../utilities";
import { Tooltip } from "../Tooltip";
import { Table } from "./Table";

/** Profile training row data structure */
interface ProfileTrainingTableRow extends BaseTableRow {
  completedDate?: string;
  type?: string;
  name?: string;
  status?: string;
  statusValue?: number;
  isTraining?: boolean;
  hasActions?: boolean;
  hasDocuments?: boolean;
  organizationIntegrationAbbreviation?: string;
  associatedCompetencies?: unknown[];
}

/** Props specific to ProfileTrainingTable */
interface ProfileTrainingTableProps extends TableProps<ProfileTrainingTableRow> {
  onRowSelect?: (event: SyntheticEvent, row: ProfileTrainingTableRow) => void;
  onRowAction?: (event: SyntheticEvent, row: ProfileTrainingTableRow) => void;
  onOpenTaskAttributesDialog?: (
    value: GridRenderCellParams<ProfileTrainingTableRow>,
    type: string
  ) => void;
  showDialog?: (associatedRow: ProfileTrainingTableRow) => void;
  rowHasAction?: (row: ProfileTrainingTableRow) => boolean;
}

export class ProfileTrainingTable extends Table<
  ProfileTrainingTableRow,
  ProfileTrainingTableProps
> {
  static columns = {
    completedDate: {
      field: "completedDate",
      label: "tableColumns.completed"
    },
    name: {
      field: "TRAINING",
      label: "tableColumns.name"
    },
    type: {
      field: "type",
      label: "tableColumns.type"
    },
    competencies: {
      field: "competencies",
      label: "tableColumns.competencies"
    },
    status: {
      field: "status",
      label: "tableColumns.status"
    },
    actions: {
      field: "actions",
      label: ""
    },
    moreActions: {
      field: "actions",
      label: ""
    },
    attachmentIcon: {
      field: "attachmentIcon",
      label: "tableColumns.attachment"
    }
  };

  static defaultProps: Partial<ProfileTrainingTableProps> = {
    ...Table.defaultProps,
    columns: [
      { type: ProfileTrainingTable.columns.completedDate },
      { type: ProfileTrainingTable.columns.type },
      { type: ProfileTrainingTable.columns.name },
      { type: ProfileTrainingTable.columns.status },
      { type: ProfileTrainingTable.columns.actions }
    ],
    noRowsMessage: "",
    rowHasAction: undefined
  };

  constructor(props: ProfileTrainingTableProps) {
    super(props);
  }

  getRowId(e: ProfileTrainingTableRow): string {
    return this.tableId + "_" + e.id;
  }

  onRowClick(params: GridRowParams<ProfileTrainingTableRow>, e: SyntheticEvent): void {
    const { onRowSelect } = this.props;
    if (params.row && onRowSelect) onRowSelect(e, params.row);
  }

  handleOnOpenAttributesDialog = (
    value: GridRenderCellParams<ProfileTrainingTableRow>,
    type: string
  ): void => {
    const { onOpenTaskAttributesDialog } = this.props;
    onOpenTaskAttributesDialog?.(value, type);
  };

  initColumns(): void {
    const { columns, sortable, rowHasAction, rowNoActionIcon, readOnly, keyword } = this.props;

    this.columns = [];

    columns?.forEach((c: TableColumnDef<ProfileTrainingTableRow>) => {
      if (this.hasColumn(c)) {
        if (c.type === ProfileTrainingTable.columns.completedDate)
          this.columns.push({
            field: "completedDate",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            width: 140,
            disableReorder: true,
            valueGetter: (_value, row) =>
              row.completedDate
                ? format(new Date(parseISO(row.completedDate)), "dd-MMM-yyyy")
                : "Not Validated",
            sortComparator: (d1: string | null, d2: string | null) => {
              if (!d1 && !d2) return 0;
              if (!d1) return 1;
              if (!d2) return -1;
              return compareDesc(new Date(d2), new Date(d1));
            }
          });
        if (c.type === ProfileTrainingTable.columns.type)
          this.columns.push({
            field: "type",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            width: 160,
            disableReorder: true,
            valueGetter: (_value, row) => row.type
          });

        if (c.type === ProfileTrainingTable.columns.name)
          this.columns.push({
            field: "courseName",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            disableReorder: true,
            flex: 1,
            filterable: false,
            valueGetter: (_value, row) => row.name,
            renderCell: (params) => highlightKeyword(params.row.name || "", keyword || "")
          });
        if (c.type === ProfileTrainingTable.columns.status)
          this.columns.push({
            field: "status",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            width: 220,
            disableReorder: true,
            valueGetter: (_value, row) => {
              const status = row.status;
              // Translate if it's a translation key (starts with "status.")
              if (status && this.props.translate && status.startsWith("status.")) {
                return this.props.translate(status);
              }
              return status;
            }
          });

        if (c.type === ProfileTrainingTable.columns.actions && !readOnly)
          this.columns.push({
            field: "actions",
            type: "actions",
            filterable: false,
            align: "right",
            width: 50,
            disableReorder: true,
            getActions: (params) => [
              rowHasAction && rowHasAction(params.row) ? (
                params.row.isTraining &&
                params.row.statusValue !== EmployeeTrainingStatus.Approved ? (
                  <GridActionsCellItem
                    icon={<MoreVertIcon />}
                    label={this.props.translate?.("common.actions") ?? "Actions"}
                    onClick={(e) => this.props.onRowAction?.(e, params.row)}
                  />
                ) : (
                  <></>
                )
              ) : rowNoActionIcon ? (
                <Tooltip
                  title={this.props.translate?.("status.underReview") ?? "Under review"}
                  placement='right'
                >
                  {rowNoActionIcon}
                </Tooltip>
              ) : (
                <div></div>
              )
            ]
          });
        if (c.type === ProfileTrainingTable.columns.moreActions)
          this.columns.push({
            field: "actions",
            type: "actions",
            filterable: false,
            align: "right",
            width: 50,
            disableReorder: true,
            getActions: (params) => [
              ...((params.row.hasActions ?? true)
                ? [
                    <GridActionsCellItem
                      icon={<MoreVertIcon />}
                      label='Actions'
                      onClick={(e) => {
                        this.props.onRowAction?.(e, params.row);
                      }}
                      key={Math.random().toString(36).substring(7)}
                    />
                  ]
                : [])
            ]
          });

        if (c.type === ProfileTrainingTable.columns.attachmentIcon)
          this.columns.push({
            field: "attachmentIcon",
            headerName: "",
            visibleFromBreakpoint:
              c.visibleFromBreakpoint ||
              (c.type as { visibleFromBreakpoint?: string }).visibleFromBreakpoint,
            width: 44,
            minWidth: 44,
            renderCell: (params) =>
              params.row.hasDocuments && <AttachmentIcon className='klayo-attrtable_attachment' />
          });
      }
    });
  }
}
